import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../services/api';
import { openSharedEventStream } from '../services/sharedEventStream';
import {
  UserAction,
  UserActionStatus,
  UserActionEvent,
  userActionService,
  getUserContext,
} from '../services/userActionService';

interface ProcessMonitorState {
  actions: UserAction[];
  activeCount: number;
  /** Recently completed/failed actions for toast notifications */
  notifications: UserAction[];
  dismissAction: (id: string) => void;
  dismissAll: () => void;
  /** Remove a notification from the toast queue */
  dismissNotification: (id: string) => void;
  isConnected: boolean;
}

const ProcessMonitorContext = createContext<ProcessMonitorState>({
  actions: [],
  activeCount: 0,
  notifications: [],
  dismissAction: () => {},
  dismissAll: () => {},
  dismissNotification: () => {},
  isConnected: false,
});

export const useProcessMonitor = () => useContext(ProcessMonitorContext);

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3071/api/catalog';

const ACTION_EVENTS = ['action:created', 'action:updated', 'action:completed', 'action:failed'] as const;

export const ProcessMonitorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [actions, setActions] = useState<UserAction[]>([]);
  const [notifications, setNotifications] = useState<UserAction[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  // Fetch initial actions on mount
  useEffect(() => {
    userActionService.getRecentActions(30).then(setActions).catch(console.error);
  }, []);

  // Live updates: one stream per browser, shared by every open tab (see sharedEventStream).
  useEffect(() => {
    const ctx = getUserContext();
    if (!ctx) return undefined;

    return openSharedEventStream({
      key: `user-actions:${API_BASE_URL}:${ctx.tenantId}:${ctx.userId}`,
      // EventSource cannot send headers; the backend accepts ?access_token= only
      // on event-stream requests.
      buildUrl: () => {
        const current = getUserContext();
        if (!current) return null;
        const accessToken = getAuthToken();
        return `${API_BASE_URL}/user-actions/stream?userId=${encodeURIComponent(current.userId)}&tenantId=${encodeURIComponent(current.tenantId)}${accessToken ? `&access_token=${encodeURIComponent(accessToken)}` : ''}`;
      },
      eventNames: ACTION_EVENTS,
      onConnectedChange: setIsConnected,
      onEvent: (eventName, data) => {
        const { action } = JSON.parse(data) as UserActionEvent;
        if (eventName === 'action:created') {
          setActions((prev) => [action, ...prev]);
          return;
        }
        setActions((prev) => prev.map((a) => (a._id === action._id ? action : a)));
        if (eventName === 'action:completed' || eventName === 'action:failed') {
          // Add to notification queue for toast
          setNotifications((prev) => [action, ...prev].slice(0, 5));
        }
      },
    });
  }, []);

  const dismissAction = useCallback((id: string) => {
    setActions((prev) => prev.filter((a) => a._id !== id));
    userActionService.dismissAction(id).catch(console.error);
  }, []);

  const dismissAll = useCallback(() => {
    setActions((prev) =>
      prev.filter(
        (a) =>
          a.status === UserActionStatus.PENDING ||
          a.status === UserActionStatus.IN_PROGRESS,
      ),
    );
    userActionService.dismissAll().catch(console.error);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n._id !== id));
  }, []);

  const activeCount = actions.filter(
    (a) =>
      a.status === UserActionStatus.PENDING ||
      a.status === UserActionStatus.IN_PROGRESS,
  ).length;

  return (
    <ProcessMonitorContext.Provider
      value={{
        actions,
        activeCount,
        notifications,
        dismissAction,
        dismissAll,
        dismissNotification,
        isConnected,
      }}
    >
      {children}
    </ProcessMonitorContext.Provider>
  );
};
